import dayjs from "dayjs";
import styles from "../MoveOutChecklist.module.css";

export const currency = (value) =>
    new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
    }).format(Math.abs(parseFloat(value) || 0));

const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const thStyle = { textAlign: "left", padding: "6px 0", fontSize: 11, fontWeight: 600, color: "#6B7280" };
const thRight = { ...thStyle, textAlign: "right" };
const rowStyle = { borderBottom: "1px solid #E5E7EB" };
const tdRight = { padding: "8px 0", textAlign: "right", fontVariantNumeric: "tabular-nums" };
const noteStyle = { fontSize: 12, color: "#6B7280", marginBottom: 10 };

// Positive values are charges to the tenant, negative values are credits
const signedRow = (label, value) => {
    const v = parseFloat(value) || 0;
    const isCharge = v > 0;
    return (
        <div className={styles.sr}>
            <span className={styles.sk}>{label}</span>
            <span className={`${styles.sv} ${v === 0 ? "" : isCharge ? styles.charge : styles.credit}`}>
                {v === 0 ? currency(0) : `${isCharge ? "-" : "+"}${currency(v)}`}
            </span>
        </div>
    );
};

/*
 * The final statement breakdown: rent adjustment, voided future bills, damages and
 * deposit settlement. Used by the Final statement step (live preview, with the rent
 * type switch passed in) and by the completed screen (the statement as it was sent).
 * Sign convention: positive = tenant owes, negative = refund to tenant.
 */
const FinalStatementBreakdown = ({ statement, moveoutDate, rentTypeSwitch = null, voidedPending = false }) => {
    const finalBalance = parseFloat(statement?.final_balance) || 0;
    const isRefund = finalBalance < 0;
    const isDue = finalBalance > 0;
    const isProrated = statement?.rent_type !== "full" && !statement?.is_full_stay;
    const voidedItems = statement?.voided_items || [];
    const monthLabel = dayjs(moveoutDate).format("MMM YYYY");

    return (
        <>
            <div className={styles.sc}>
                <div className={styles.scTitle}>
                    Rent adjustment — {monthLabel}
                </div>

                {rentTypeSwitch}

                <div className={styles.sr}>
                    <span className={styles.sk}>Move-out date</span>
                    <span className={styles.sv}>{dayjs(moveoutDate).format("MMM DD, YYYY")}</span>
                </div>

                <div className={styles.sr}>
                    <span className={styles.sk}>Days occupied in month</span>
                    <span className={styles.sv}>
                        {statement?.days_occupied ?? dayjs(moveoutDate).date()} of{" "}
                        {statement?.days_in_month ?? dayjs(moveoutDate).daysInMonth()} days
                    </span>
                </div>

                {statement && (
                    <>
                        <div className={styles.sr}>
                            <span className={styles.sk}>Monthly rent</span>
                            <span className={styles.sv}>{currency(statement.monthly_rent)}</span>
                        </div>

                        <div className={styles.sr}>
                            <span className={styles.sk}>
                                {isProrated
                                    ? `Prorated rent (${statement.days_occupied} days × ${currency(statement.daily_rate)}/day)`
                                    : "Full month rent"}
                            </span>
                            <span className={styles.sv}>{currency(statement.final_month_charge)}</span>
                        </div>

                        <div className={styles.sr}>
                            <span className={styles.sk}>Payments received for {monthLabel}</span>
                            <span className={`${styles.sv} ${styles.credit}`}>{currency(statement.final_month_paid)}</span>
                        </div>

                        {signedRow(
                            statement.final_month_net >= 0 ? `Net due for ${monthLabel}` : `Net credit for ${monthLabel}`,
                            statement.final_month_net
                        )}
                    </>
                )}
            </div>

            {voidedItems.length > 0 && (
                <div className={styles.sc}>
                    <div className={styles.scTitle}>Voided future bills</div>

                    <div style={noteStyle}>
                        These bills are dated after the move-out date and {voidedPending ? "will be" : "were"} voided. They are not included in the previous balance or the final balance.
                    </div>

                    <table style={tableStyle}>
                        <thead>
                            <tr style={rowStyle}>
                                <th style={thStyle}>Bill</th>
                                <th style={thStyle}>Date</th>
                                <th style={thRight}>Paid</th>
                                <th style={thRight}>Amount</th>
                            </tr>
                        </thead>

                        <tbody>
                            {voidedItems.map((item, index) => (
                                <tr key={item?.id || index} style={rowStyle}>
                                    <td style={{ padding: "8px 0" }}>
                                        <div style={{ fontWeight: 500 }}>{item?.description || "Bill"}</div>
                                        {item?.payment_type && (
                                            <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>{item.payment_type}</div>
                                        )}
                                    </td>
                                    <td style={{ padding: "8px 0", whiteSpace: "nowrap" }}>
                                        {item?.date ? dayjs(item.date).format("MMM DD, YYYY") : "—"}
                                    </td>
                                    <td style={tdRight}>{item?.paid_amount > 0 ? currency(item.paid_amount) : "—"}</td>
                                    <td style={tdRight}>{currency(item?.amount)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className={styles.sr} style={{ marginTop: 8, paddingTop: 10 }}>
                        <span className={styles.sk}>Total voided (not charged)</span>
                        <span className={styles.sv}>{currency(statement?.void_total)}</span>
                    </div>
                </div>
            )}

            {statement?.damages?.length > 0 && (
                <div className={styles.sc}>
                    <div className={styles.scTitle}>Damage detail</div>

                    <table style={tableStyle}>
                        <thead>
                            <tr style={rowStyle}>
                                <th style={thStyle}>Item</th>
                                <th style={thRight}>Cost</th>
                            </tr>
                        </thead>

                        <tbody>
                            {statement.damages.map((d, i) => (
                                <tr key={i} style={rowStyle}>
                                    <td style={{ padding: "7px 0" }}>{d?.description || "—"}</td>
                                    <td style={tdRight}>{currency(d?.cost)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className={styles.sr}>
                        <span className={styles.sk}>Total damage charges</span>
                        <span className={`${styles.sv} ${styles.charge}`} style={{ fontSize: 16 }}>
                            -{currency(statement.damage_total)}
                        </span>
                    </div>
                </div>
            )}

            {statement && (
                <div className={styles.sc}>
                    <div className={styles.scTitle}>Deposit settlement</div>

                    {signedRow(
                        statement.prior_balance >= 0 ? "Previous outstanding balance" : "Previous credit balance",
                        statement.prior_balance
                    )}

                    {signedRow(
                        statement.final_month_net >= 0 ? `${monthLabel} rent due` : `${monthLabel} rent credit`,
                        statement.final_month_net
                    )}

                    {signedRow("Security deposit refund", -statement.security_deposit)}

                    {statement.damage_total > 0 && signedRow("Damage charges (tenant)", statement.damage_total)}

                    <div className={styles.sr}>
                        <span className={styles.sk}>
                            {isRefund ? "Refund to tenant" : isDue ? "Balance due from tenant" : "Account settled"}
                        </span>
                        <span
                            className={`${styles.sv} ${isRefund ? styles.credit : isDue ? styles.charge : ""}`}
                            style={{ fontSize: 16 }}
                        >
                            {currency(finalBalance)}
                        </span>
                    </div>
                </div>
            )}
        </>
    );
};

export default FinalStatementBreakdown;
